"use client"

import { useState } from "react"
import type { Task, TimesheetEntry } from "@/lib/types"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { formatDate, formatTime, formatDuration, calculateDuration, formatDurationFromSeconds } from "@/lib/time-utils"
import { Calendar, Clock, Edit2, Plus, Trash2, Filter, Eye, EyeOff, Search } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useTimer } from "@/lib/timer-context"

interface TimesheetListProps {
  timesheets: TimesheetEntry[]
  tasks: Task[]
  onUpdate: (entry: TimesheetEntry) => void
  onDelete: (entryId: string) => void
}

export function TimesheetList({ timesheets, tasks, onUpdate, onDelete }: TimesheetListProps) {
  const { currentEntry: activeEntry, elapsedSeconds, isRunning } = useTimer()

  const [editingEntry, setEditingEntry] = useState<TimesheetEntry | null>(null)
  const [isAddingManual, setIsAddingManual] = useState(false)
  const [manualMinutes, setManualMinutes] = useState("")
  const [selectedTaskId, setSelectedTaskId] = useState("")

  const [filterTask, setFilterTask] = useState<string>("all")
  const [filterDate, setFilterDate] = useState<string>("")
  const [searchTerm, setSearchTerm] = useState("")
  const [showFilters, setShowFilters] = useState(false)
  const [showSubmitted, setShowSubmitted] = useState(false)
  const [taskSearchTerm, setTaskSearchTerm] = useState("")
  const [manualTaskSearchTerm, setManualTaskSearchTerm] = useState("")

  const getTaskById = (taskId: string) => tasks.find((t) => t.id === taskId)

  const handleSaveEdit = () => {
    if (editingEntry) {
      if (editingEntry.endTime && editingEntry.startTime >= editingEntry.endTime) {
        alert("End time must be after start time")
        return
      }
  // saving edited entry
  // duration: calculateDuration(editingEntry.startTime, editingEntry.endTime)
      onUpdate(editingEntry)
      setEditingEntry(null)
    }
  }

  const handleAddManual = () => {
    if (!selectedTaskId || !manualMinutes) return

    const newEntry: TimesheetEntry = {
      id: `manual-${Date.now()}`,
      taskId: selectedTaskId,
      userId: "user-1",
      status: "Draft",
      startTime: new Date().toISOString(),
      endTime: new Date().toISOString(),
      manualMinutes: Number.parseInt(manualMinutes),
      isSubmitted: false,
    }

    onUpdate(newEntry)
    setIsAddingManual(false)
    setManualMinutes("")
    setSelectedTaskId("")
  }

  const filteredTimesheets = timesheets.filter((entry) => {
    if (!showSubmitted && entry.status === "Submitted") return false
    if (filterTask !== "all" && entry.taskId !== filterTask) return false
    if (filterDate) {
      const entryDate = new Date(entry.startTime).toISOString().split("T")[0]
      if (entryDate !== filterDate) return false
    }
    
    // Filter by search term
    if (searchTerm.trim()) {
      const task = getTaskById(entry.taskId)
      const matchesSearch = (task?.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                           (task?.description?.toLowerCase() || '').includes(searchTerm.toLowerCase())
      if (!matchesSearch) return false
    }
    
    return true
  })

  const sortedTimesheets = [...filteredTimesheets].sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground dark:text-gray-100 mb-1">Timesheet</h2>
          <p className="text-muted-foreground dark:text-gray-400">View and manage your time entries</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={showSubmitted ? "default" : "outline"}
            onClick={() => setShowSubmitted(!showSubmitted)}
            className="gap-2"
          >
            {showSubmitted ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            {showSubmitted ? "Hide" : "Show"} Submitted
          </Button>
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </Button>
          <Button onClick={() => setIsAddingManual(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Manual Entry
          </Button>
        </div>
      </div>

      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search timesheet entries by task..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 max-w-md"
        />
      </div>

      {showFilters && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Filter by Task</Label>
                <Select value={filterTask} onValueChange={setFilterTask}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="flex items-center border-b px-3 pb-2">
                      <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                      <input
                        placeholder="Search tasks..."
                        className="flex h-10 w-full rounded-md bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                        value={taskSearchTerm}
                        onChange={(e) => setTaskSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                      <SelectItem value="all">All Tasks</SelectItem>
                      {tasks
                        .filter((task) =>
                          task.title.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
                          (task.description && task.description.toLowerCase().includes(taskSearchTerm.toLowerCase()))
                        )
                        .map((task) => (
                          <SelectItem key={task.id} value={task.id}>
                            {task.title}
                          </SelectItem>
                        ))}
                      {tasks.filter((task) =>
                        task.title.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
                        (task.description && task.description.toLowerCase().includes(taskSearchTerm.toLowerCase()))
                      ).length === 0 && taskSearchTerm && (
                        <div className="py-6 text-center text-sm text-muted-foreground">
                          No tasks found
                        </div>
                      )}
                    </div>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Filter by Date</Label>
                <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} />
              </div>
            </div>
            {(filterTask !== "all" || filterDate) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilterTask("all")
                  setFilterDate("")
                }}
                className="mt-4"
              >
                Clear Filters
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {activeEntry && (
          <Card className="border-2 border-primary dark:bg-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-foreground dark:text-gray-100">
                      {getTaskById(activeEntry.taskId)?.title || "Unknown Task"}
                    </h3>
                    <Badge variant="secondary" className="bg-green-500/10 text-green-500 border-green-500/20">
                      {isRunning ? "⏱️ Running" : "⏸️ Paused"}
                    </Badge>
                  </div>

                  <div className="flex items-center gap-6 text-sm text-muted-foreground dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {formatDate(activeEntry.startTime)}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      {formatTime(activeEntry.startTime)} - In Progress
                    </div>
                    <div className="font-semibold text-foreground dark:text-gray-100 text-lg">
                      {formatDurationFromSeconds(elapsedSeconds)}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {sortedTimesheets.map((entry) => {
          const task = getTaskById(entry.taskId)
          const duration = calculateDuration(entry.startTime, entry.endTime || null) + (entry.manualMinutes || 0)

          return (
            <Card key={entry.id} className="dark:bg-gray-800">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-foreground dark:text-gray-100">{task?.title || "Unknown Task"}</h3>
                      <Badge variant={entry.status === "Submitted" ? "default" : "secondary"}>{entry.status}</Badge>
                    </div>

                    <div className="flex items-center gap-6 text-sm text-muted-foreground dark:text-gray-400">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {formatDate(entry.startTime)}
                      </div>
                      {entry.endTime ? (
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          {formatTime(entry.startTime)} - {formatTime(entry.endTime)}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Manual Entry
                        </div>
                      )}
                      <div className="font-semibold text-foreground dark:text-gray-100">{formatDuration(duration)}</div>
                    </div>

                    {(entry.manualMinutes || 0) > 0 && (
                      <p className="text-sm text-muted-foreground dark:text-gray-400">
                        Includes {formatDuration(entry.manualMinutes || 0)} manual time
                      </p>
                    )}
                  </div>

                  {entry.status === "Draft" && (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditingEntry(entry)} className="gap-2">
                        <Edit2 className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDelete(entry.id)}
                        className="gap-2 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {sortedTimesheets.length === 0 && (
        <div className="text-center py-12 text-muted-foreground dark:text-gray-400">
          {!showSubmitted && timesheets.some((t) => t.status === "Submitted")
            ? "No draft entries. Click 'Show Submitted' to view submitted entries."
            : "No time entries match your filters"}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingEntry} onOpenChange={() => setEditingEntry(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Time Entry</DialogTitle>
            <DialogDescription>Modify the task, start time, end time, or add manual minutes</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Task</Label>
              <Select
                value={editingEntry?.taskId}
                onValueChange={(value) => setEditingEntry((prev) => (prev ? { ...prev, taskId: value } : null))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tasks.map((task) => (
                    <SelectItem key={task.id} value={task.id}>
                      {task.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Time</Label>
              <Input
                type="time"
                value={
                  editingEntry?.startTime
                    ? new Date(editingEntry.startTime).toLocaleTimeString('en-US', { 
                        hour12: false, 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })
                    : ""
                }
                onChange={(e) => {
                  if (!editingEntry) return
                  const [hours, minutes] = e.target.value.split(":")
                  const newDate = new Date(editingEntry.startTime)
                  newDate.setHours(Number.parseInt(hours), Number.parseInt(minutes), 0, 0)
                  setEditingEntry({ ...editingEntry, startTime: newDate.toISOString() })
                }}
              />
            </div>

            <div className="space-y-2">
              <Label>End Time</Label>
              <Input
                type="time"
                value={
                  editingEntry?.endTime
                    ? new Date(editingEntry.endTime).toLocaleTimeString('en-US', { 
                        hour12: false, 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })
                    : ""
                }
                onChange={(e) => {
                  if (!editingEntry || !editingEntry.endTime) return
                  const [hours, minutes] = e.target.value.split(":")
                  const newDate = new Date(editingEntry.endTime)
                  newDate.setHours(Number.parseInt(hours), Number.parseInt(minutes), 0, 0)
                  setEditingEntry({ ...editingEntry, endTime: newDate.toISOString() })
                }}
              />
            </div>

            {editingEntry && editingEntry.id.startsWith('manual-') && (
              <div className="space-y-2">
                <Label>Additional Manual Minutes</Label>
                <Input
                  type="number"
                  value={editingEntry?.manualMinutes || 0}
                  onChange={(e) =>
                    setEditingEntry((prev) =>
                      prev ? { ...prev, manualMinutes: Number.parseInt(e.target.value) || 0 } : null,
                    )
                  }
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingEntry(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Manual Entry Dialog */}
      <Dialog open={isAddingManual} onOpenChange={setIsAddingManual}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Manual Time Entry</DialogTitle>
            <DialogDescription>Add time manually for a task</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Task</Label>
              <Select value={selectedTaskId} onValueChange={setSelectedTaskId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a task" />
                </SelectTrigger>
                <SelectContent>
                  <div className="flex items-center border-b px-3 pb-2">
                    <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                    <input
                      placeholder="Search tasks..."
                      className="flex h-10 w-full rounded-md bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                      value={manualTaskSearchTerm}
                      onChange={(e) => setManualTaskSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="max-h-[300px] overflow-y-auto">
                    {tasks
                      .filter((task) => task.status !== "Done")
                      .filter((task) =>
                        task.title.toLowerCase().includes(manualTaskSearchTerm.toLowerCase()) ||
                        (task.description && task.description.toLowerCase().includes(manualTaskSearchTerm.toLowerCase()))
                      )
                      .map((task) => (
                        <SelectItem key={task.id} value={task.id}>
                          {task.title}
                        </SelectItem>
                      ))}
                    {tasks
                      .filter((task) => task.status !== "Done")
                      .filter((task) =>
                        task.title.toLowerCase().includes(manualTaskSearchTerm.toLowerCase()) ||
                        (task.description && task.description.toLowerCase().includes(manualTaskSearchTerm.toLowerCase()))
                      ).length === 0 && (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        {manualTaskSearchTerm ? "No tasks found" : "No active tasks available"}
                      </div>
                    )}
                  </div>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Minutes</Label>
              <Input
                type="number"
                placeholder="Enter minutes"
                value={manualMinutes}
                onChange={(e) => setManualMinutes(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddingManual(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddManual}>Add Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
